import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const RideMatchMap = ({ tripDetails, driverLocations = [], driverNames = {}, height = '500px' }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const map = L.map(mapRef.current).setView([11.05, 77.0], 6);
    mapInstanceRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !map._container) return;

    let isMounted = true;

    // Clear existing layers
    map.eachLayer((layer) => {
      if (layer instanceof L.Marker || layer instanceof L.Polyline) {
        map.removeLayer(layer);
      }
    });

    const coordinates = [];

    // Get pickup location from stops
    const pickupLocation = tripDetails?.stops?.find(stop => 
      stop.name === 'Pickup Point' || stop.name === 'pickup' || stop.name === 'Pickup'
    )?.location || tripDetails?.startLocation;

    // Add pickup location marker
    if (pickupLocation && Array.isArray(pickupLocation) && pickupLocation.length === 2) {
      const [pickupLng, pickupLat] = pickupLocation;
      const pickupIcon = L.divIcon({
        html: `<div style="background-color: #10B981; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
        className: 'custom-marker',
        iconSize: [24, 24],
        iconAnchor: [12, 12]
      });
      L.marker([pickupLat, pickupLng], { icon: pickupIcon })
        .bindPopup(`<b>Pickup Point</b><br/>${pickupLat.toFixed(6)}, ${pickupLng.toFixed(6)}`)
        .addTo(map);
      coordinates.push([pickupLat, pickupLng]);
    }

    // Calculate route distance using Valhalla API
    const calculateRouteDistance = async (lat1, lon1, lat2, lon2) => {
      try {
        const response = await fetch('https://valhalla1.openstreetmap.de/route', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            locations: [
              { lat: lat1, lon: lon1 },
              { lat: lat2, lon: lon2 }
            ],
            costing: 'bus',
            units: 'kilometers'
          })
        });
        const data = await response.json();
        return data.trip?.summary?.length?.toFixed(2) || 'N/A';
      } catch (error) {
        console.error('Distance calculation error:', error);
        return 'N/A';
      }
    };

    // Add driver locations from ride match log
    if (tripDetails?.rideMatchLog && tripDetails.rideMatchLog.length > 0) {
      // Get ranked drivers event
      const rankedDriversEvent = tripDetails.rideMatchLog.find(log => log.event === 'ranked_drivers');
      const rankedDrivers = rankedDriversEvent?.top_drivers || [];
      
      // Get available drivers event for coordinates
      const availableDriversEvent = tripDetails.rideMatchLog.find(log => log.event === 'available_drivers');
      const availableDrivers = availableDriversEvent?.drivers || [];
      
      // Create map of driver_id to rank, name and coordinates
      const driverDataMap = {};
      rankedDrivers.forEach(driver => {
        const driverId = driver.driver_id?.toString();
        if (driverId) {
          driverDataMap[driverId] = { 
            rank: driver.rank, 
            name: driverNames[driverId]?.name || driver.driver_name || driver.name 
          };
        }
      });
      
      // Add coordinates and name from available drivers
      availableDrivers.forEach(driver => {
        const driverId = driver.driver_id?.toString();
        if (driverId && driver.location?.coordinates) {
          if (!driverDataMap[driverId]) {
            driverDataMap[driverId] = {};
          }
          driverDataMap[driverId].coordinates = driver.location.coordinates;
          if (!driverDataMap[driverId].name) {
            driverDataMap[driverId].name = driverNames[driverId]?.name || driver.driver_name || driver.name;
          }
        }
      });
      
      // Also check individual log entries for coordinates and name
      tripDetails.rideMatchLog.forEach(log => {
        const driverId = log.driver_id?.toString();
        if (driverId) {
          if (!driverDataMap[driverId]) {
            driverDataMap[driverId] = {};
          }
          if (log.coordinates && !driverDataMap[driverId].coordinates) {
            driverDataMap[driverId].coordinates = log.coordinates;
          }
          if (!driverDataMap[driverId].name) {
            driverDataMap[driverId].name = driverNames[driverId]?.name || log.driver_name || log.name;
          }
        }
      });
      
      // Create ordered list of drivers with coordinates
      const orderedDriverLogs = Object.entries(driverDataMap)
        .filter(([driverId, data]) => data.coordinates && Array.isArray(data.coordinates) && data.coordinates.length === 2)
        .map(([driverId, data]) => ({
          driver_id: driverId,
          coordinates: data.coordinates,
          rank: data.rank,
          name: data.name
        }))
        .sort((a, b) => {
          if (a.rank && b.rank) return a.rank - b.rank;
          if (a.rank) return -1;
          if (b.rank) return 1;
          return 0;
        });

      (async () => {
        for (let index = 0; index < orderedDriverLogs.length; index++) {
          if (!isMounted || !mapInstanceRef.current) break;
          
          const log = orderedDriverLogs[index];
          const [lng, lat] = log.coordinates.map(Number); // GeoJSON format: [lng, lat]
          
          // Calculate distance to pickup
          let distanceToPickup = 'Calculating...';
          if (pickupLocation && Array.isArray(pickupLocation) && pickupLocation.length === 2) {
            const [pickupLng, pickupLat] = pickupLocation;
            const distance = await calculateRouteDistance(lat, lng, pickupLat, pickupLng);
            distanceToPickup = distance !== 'N/A' ? distance + ' km' : 'N/A';
          }

          if (!isMounted || !mapInstanceRef.current) break;

          // Get all events for this driver
          const driverEvents = tripDetails.rideMatchLog.filter(l => l.driver_id?.toString() === log.driver_id?.toString());
          
          const displayNumber = log.rank || (index + 1);
          const driverIcon = L.divIcon({
            html: `<div style="background-color: #8B5CF6; width: 28px; height: 28px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 12px;">${displayNumber}</div>`,
            className: 'custom-marker',
            iconSize: [28, 28],
            iconAnchor: [14, 14]
          });
          
          const eventsHtml = driverEvents.map(event => 
            `<div style="margin: 5px 0; padding: 5px; background: #f3f4f6; border-radius: 4px;">
              <b>${event.event?.replace(/_/g, ' ').toUpperCase()}</b><br/>
              ${event.response ? `Response: ${event.response}<br/>` : ''}
              ${event.timestamp ? `Time: ${new Date(event.timestamp).toLocaleString()}<br/>` : ''}
            </div>`
          ).join('');
          
          const popupContent = `
            <div style="min-width: 250px; max-height: 300px; overflow-y: auto;">
              <b>Driver ${displayNumber}</b>${log.rank ? ` (Rank: ${log.rank})` : ''}<br/>
              <b>Driver Name:</b> ${log.name || 'Unknown'}<br/>
              <b>Location:</b> ${lat.toFixed(6)}, ${lng.toFixed(6)}<br/>
              <b>Distance to Pickup:</b> ${distanceToPickup}<br/>
              <br/><b>Events:</b><br/>
              ${eventsHtml || 'No events'}
            </div>
          `;
          
          L.marker([lat, lng], { icon: driverIcon })
            .bindPopup(popupContent)
            .addTo(map);
          
          coordinates.push([lat, lng]);
        }

        // Fit map to show all markers
        if (isMounted && mapInstanceRef.current && coordinates.length > 0) {
          if (coordinates.length === 1) {
            map.setView(coordinates[0], 13);
          } else {
            const bounds = L.latLngBounds(coordinates);
            map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
          }
        }
      })();
    } else {
      // Fit map to show all markers (for pickup only)
      if (coordinates.length > 0) {
        if (coordinates.length === 1) {
          map.setView(coordinates[0], 13);
        } else {
          const bounds = L.latLngBounds(coordinates);
          map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
        }
      }
    }

    return () => {
      isMounted = false;
    };
  }, [tripDetails, driverLocations, driverNames]);

  return (
    <div style={{ height, width: '100%' }}>
      <div ref={mapRef} style={{ height: '100%', width: '100%', borderRadius: '8px' }}></div>
    </div>
  );
};

export default RideMatchMap;
